import { useCallback, useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import {
  Box,
  Chip,
  IconButton,
  ThemeProvider,
  Typography,
  useTheme,
  Tooltip,
  Portal,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  useExecutionQuery,
  usePlayerHistoryQuery,
} from "../../tanstack/hooks/usePlayerQuery";
import { useSearchParams } from "react-router";
import { IYantraTicket } from "../../entities/yantras/yantras";
import {
  FormattedDate,
  FormattedNumber,
  FormattedTime,
  useIntl,
} from "react-intl";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { localTheme, MRTBox, tableStyles } from "./HistoryTheme";
import { useTranslate } from "../../hooks/useTranslate";
import { useTableTranslate } from "../../hooks/useTableTranslate";
import Loader from "../Loader";
import { getYantrasRows } from "../../utils/transformDataToColumns";
import { TranslationKey } from "../../hooks/useLocale";
import { useStatusStore } from "../../stores/statusStore/useStatusStore";
import { YantraExecutionInfo } from "../../tanstack/queries/player";
import { useQueryClient } from "@tanstack/react-query";
import Big from "big.js";

interface IProps {
  onClose: (event: React.MouseEvent<HTMLElement>) => void;
}

type RoundBet = {
  id: string;
  type: string;
  name: string;
  amount: number;
  price: number;
  total: number;
  payout: number;
  status: string;
};

function HistoryTable({ onClose }: IProps) {
  const [params] = useSearchParams();
  const [selectedRound, setSelectedRound] = useState<IYantraTicket | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslate();
  const { formatNumber } = useIntl();
  const theme = useTheme();

  const { data: history, isLoading: historyLoading } = usePlayerHistoryQuery(
    params.get("pyramidsid") || "",
  );

  const { data: execution, isLoading: executionLoading } = useExecutionQuery(
    selectedRound?.yantraExecutionId || "",
    params.get("pyramidsid") || "",
    {
      enabled: !!selectedRound?.yantraExecutionId,
    },
  );

  // get active execution for example
  const queryClient = useQueryClient();
  const { activeExecutionId } = useStatusStore();
  const activeExecutionData = queryClient.getQueryData<{
    yantraExecution: YantraExecutionInfo;
  }>(["execution", activeExecutionId]);

  // Get the latest execution data for rules display
  const yantrasCount =
    activeExecutionData?.yantraExecution?.yantras?.length || 0;
  const rowsCount = activeExecutionData?.yantraExecution?.yantraRows || 0;
  const yantraPayout = activeExecutionData?.yantraExecution?.payout || 0;
  const rowPayout = activeExecutionData?.yantraExecution?.rowPayout || 0;
  const yantraPrice = activeExecutionData?.yantraExecution?.price || 0;
  const rowPrice = activeExecutionData?.yantraExecution?.rowPrice || 0;

  const yantraMultiplier = parseFloat(
    new Big(yantraPayout).div(yantraPrice).toFixed(2),
  );
  const rowMultiplier = parseFloat(new Big(rowPayout).div(rowPrice).toFixed(2));

  const getCurrentExecution = useCallback(
    (yantraTickets: IYantraTicket[], id: string): IYantraTicket | null => {
      if (!yantraTickets || !id) return null;
      return (
        yantraTickets.find(
          (ticket: IYantraTicket) => ticket.yantraExecutionId === id,
        ) || null
      );
    },
    [],
  );

  const winAmount = useMemo(() => {
    const currentExecution = getCurrentExecution(
      history?.yantraTickets,
      execution?.yantraExecution?.id ?? "",
    );
    if (currentExecution) {
      return (
        currentExecution.amount * currentExecution.price +
        currentExecution.rowAmount * currentExecution.rowPrice +
        currentExecution.totalPayout
      );
    }
    return 0;
  }, [history, execution, getCurrentExecution]);

  const columns = useMemo<MRT_ColumnDef<IYantraTicket>[]>(
    () => [
      {
        accessorKey: "yantraExecutionId",
        header: t("historyTable.roundId"),
        filterVariant: "text",
        Cell: ({ cell, row }) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: { xs: 0.5, md: 1 },
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  wordBreak: "break-all",
                  fontSize: (theme) => theme.typography.pxToRem(12),
                }}
              >
                {cell.getValue<string>()}
              </Typography>
              <Tooltip title={t("historyTable.showRoundDetails")}>
                <IconButton
                  onClick={() => {
                    setSelectedRound(row.original);
                  }}
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        accessorFn: (originalRow) =>
          originalRow.amount * originalRow.price +
          originalRow.rowAmount * originalRow.rowPrice,
        header: t("historyTable.betAmount"),
        filterVariant: "range",
        filterFn: "between",
        size: 50,
        Cell: ({ cell }) => {
          return (
            <FormattedNumber
              minimumFractionDigits={2}
              maximumFractionDigits={2}
              value={cell.getValue<number>()}
            />
          );
        },
      },
      {
        accessorFn: (originalRow) =>
          originalRow.amount * originalRow.price +
          originalRow.rowAmount * originalRow.rowPrice +
          originalRow.totalPayout,
        header: t("historyTable.winAmount"),
        filterVariant: "range",
        filterFn: "between",
        size: 50,
        Cell: ({ cell }) => {
          switch (status) {
            case "PENDING":
              return "-";
            default:
              return (
                <FormattedNumber
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  value={cell.getValue<number>()}
                />
              );
          }
        },
      },
      {
        accessorFn: (originalRow) => new Date(originalRow.buyDate),
        accessorKey: "buyDate",
        header: t("historyTable.date"),
        size: 100,
        filterVariant: "datetime-range",
        Cell: ({ row }) => {
          const { buyDate } = row.original;
          return (
            <>
              <FormattedDate value={buyDate} />{" "}
              <FormattedTime value={buyDate} />
            </>
          );
        },
      },
      {
        accessorKey: "status",
        header: t("historyTable.status"),
        size: 50,
        filterFn: "equals",
        filterSelectOptions: [
          { label: t("historyTable.win"), value: "Win" },
          { label: t("historyTable.lose"), value: "Loose" },
        ],
        filterVariant: "select",
        Cell: ({ row }) => {
          const { status } = row.original;

          switch (status) {
            case "LOOSE":
              return <Chip label={t("historyTable.lose")} color="error" />;
            case "WIN":
              return <Chip label={t("historyTable.win")} color="success" />;
            case "PENDING":
              return <Chip label={t("historyTable.pending")} color="primary" />;

            default:
              break;
          }
        },
      },
    ],
    [t],
  );

  const { tableLocale } = useTableTranslate();

  const table = useMaterialReactTable({
    columns,
    data: history?.yantraTickets || [],
    localization: tableLocale,
    state: {
      isLoading: historyLoading,
    },
    enableTopToolbar: true,
    ...tableStyles,
  });

  const rowsConfiguration = useMemo(() => {
    if (execution) {
      return getYantrasRows(
        execution.yantraExecution.yantras,
        execution.yantraExecution.yantraRows,
        execution.yantraExecution.yantraCols,
      );
    }
    return [];
  }, [execution]);

  const winningColumn = useMemo(() => {
    const currentExecution = getCurrentExecution(
      history?.yantraTickets,
      execution?.yantraExecution?.id ?? "",
    );

    if (
      currentExecution &&
      currentExecution.resultIndex &&
      execution?.yantraExecution
    ) {
      const columnIndex = Math.floor(
        currentExecution.resultIndex / execution.yantraExecution.yantraCols,
      );
      const columns = getYantrasRows(
        execution.yantraExecution.yantras,
        execution.yantraExecution.yantraRows,
        execution.yantraExecution.yantraCols,
      );
      return columns[columnIndex];
    }
    return { borderColor: "#ff0000", name: "Red", cards: [] };
  }, [execution, history, getCurrentExecution]);

  const roundBetsColumns = useMemo<MRT_ColumnDef<RoundBet>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("controlBoard.bet"),
        filterVariant: "text",
        Cell: ({ cell, row }) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              {row.original.type === "yantra" ? (
                <Box
                  component="img"
                  sx={{
                    width: theme.spacing(5),
                    height: theme.spacing(5),
                  }}
                  src={
                    execution?.yantraExecution.yantras[Number(row.original.id)]
                      .image
                  }
                  alt={row.original.name}
                />
              ) : (
                <Box
                  sx={{
                    width: theme.spacing(5),
                    height: theme.spacing(5),
                    bgcolor: `${
                      rowsConfiguration[Number(row.original.id)].borderColor
                    } !important`,
                  }}
                />
              )}
              <Typography
                sx={{
                  fontFamily: "sans-serif",
                  fontSize: (theme) => theme.typography.pxToRem(12),
                }}
              >
                {cell.getValue<string>()}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "amount",
        header: t("controlBoard.amount"),
        filterVariant: "range",
        filterFn: "between",
        Cell: ({ cell }) => <FormattedNumber value={cell.getValue<number>()} />,
      },
      {
        accessorKey: "price",
        header: t("controlBoard.price"),
        filterVariant: "range",
        filterFn: "between",
        Cell: ({ cell }) => (
          <FormattedNumber
            value={cell.getValue<number>()}
            minimumFractionDigits={2}
            maximumFractionDigits={2}
          />
        ),
      },
      {
        accessorKey: "total",
        header: t("controlBoard.total"),
        filterVariant: "range",
        filterFn: "between",
        Cell: ({ cell }) => (
          <FormattedNumber
            value={cell.getValue<number>()}
            minimumFractionDigits={2}
            maximumFractionDigits={2}
          />
        ),
      },
      {
        accessorKey: "status",
        header: t("historyTable.status"),
        size: 50,
        filterFn: "equals",
        filterSelectOptions: [
          { label: t("historyTable.win"), value: "Win" },
          { label: t("historyTable.lose"), value: "Loose" },
        ],
        filterVariant: "select",
        Cell: ({ row }) => {
          const { status } = row.original;

          switch (status) {
            case "LOSE":
              return <Chip label={t("historyTable.lose")} color="error" />;
            case "WIN":
              return <Chip label={t("historyTable.win")} color="success" />;
            case "PENDING":
              return <Chip label={t("historyTable.pending")} color="primary" />;

            default:
              break;
          }
        },
      },
      {
        accessorKey: "payout",
        header: t("historyTable.winAmount"),
        filterVariant: "range",
        filterFn: "between",
        Cell: ({ cell }) => (
          <FormattedNumber
            value={cell.getValue<number>()}
            minimumFractionDigits={2}
            maximumFractionDigits={2}
          />
        ),
      },
    ],
    [rowsConfiguration, execution, t, theme],
  );

  const roundBets = useMemo<Array<RoundBet>>(() => {
    if (!selectedRound || !execution) return [];

    const currentExecution = getCurrentExecution(
      history?.yantraTickets,
      selectedRound?.yantraExecutionId,
    );

    const bets: RoundBet[] = [];

    for (const [key, value] of Object.entries(selectedRound.images ?? {})) {
      const yantra = execution.yantraExecution.yantras?.[Number(key)];
      bets.push({
        id: key,
        type: "yantra",
        name: yantra?.name ?? "",
        amount: value,
        price: execution.yantraExecution?.price ?? 0,
        total: (execution.yantraExecution?.price ?? 0) * value,
        payout:
          Number(key) === currentExecution?.resultIndex
            ? (currentExecution?.payout ?? 0) * value
            : 0,
        status:
          currentExecution?.resultIndex === -1
            ? "PENDING"
            : Number(key) === currentExecution?.resultIndex
              ? "WIN"
              : "LOSE",
      });
    }

    const winningColumnIndex =
      currentExecution && execution.yantraExecution?.yantraCols
        ? Math.floor(
            currentExecution.resultIndex / execution.yantraExecution.yantraCols,
          )
        : -1;

    for (const [key, value] of Object.entries(selectedRound.rowImages ?? {})) {
      const rowConf = rowsConfiguration?.[Number(key)];
      bets.push({
        id: key,
        type: "row",
        name: rowConf ? t(`colors.${rowConf.name}` as TranslationKey) : "",
        amount: value,
        price: execution.yantraExecution?.rowPrice ?? 0,
        total: (execution.yantraExecution?.rowPrice ?? 0) * value,
        payout:
          Number(key) === winningColumnIndex
            ? (currentExecution?.rowPayout ?? 0) * value
            : 0,
        status:
          currentExecution?.resultIndex === -1
            ? "PENDING"
            : Number(key) === winningColumnIndex
              ? "WIN"
              : "LOSE",
      });
    }

    return bets;
  }, [
    selectedRound,
    execution,
    history,
    t,
    rowsConfiguration,
    getCurrentExecution,
  ]);

  const roundBetsTable = useMaterialReactTable({
    columns: roundBetsColumns,
    data: roundBets,
    ...tableStyles,
  });

  const winningYantra = useMemo(() => {
    const currentExecution = getCurrentExecution(
      history?.yantraTickets,
      execution?.yantraExecution?.id ?? "",
    );
    if (
      execution?.yantraExecution &&
      currentExecution &&
      Number.isInteger(currentExecution.resultIndex)
    ) {
      if (
        currentExecution.resultIndex >= 0 &&
        currentExecution.resultIndex <
          (execution.yantraExecution.yantras?.length ?? 0)
      ) {
        return execution.yantraExecution.yantras[currentExecution.resultIndex];
      }
    }
    return null;
  }, [execution, history, getCurrentExecution]);

  return (
    <Portal>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(30, 30, 30, 0.2)",
          zIndex: 20,
        }}
        onClick={onClose}
      />
      <Box
        sx={{
          m: { xs: 0, md: "5%" },
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 22,
          maxHeight: { xs: "100vh", md: "90vh" },
          borderRadius: { xs: 0, md: theme.spacing(3) },
          overflowY: "auto",
          width: { xs: "100%", md: "90%" },
          color: "white",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            bgcolor: "rgba(30, 30, 30, 0.95)",
            borderRadius: { xs: 0, md: theme.spacing(3) },
            p: { xs: 0, md: 4 },
            minHeight: { xs: "100vh", md: 0 },
          }}
        >
          {/* Close button in top right corner */}
          <Tooltip title={t("common.close")}>
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: { xs: 8, md: 16 },
                right: { xs: 8, md: 16 },
                color: "white",
                zIndex: 10,
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>

          {selectedRound ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  userSelect: "text",
                }}
              >
                <Tooltip title={t("common.back")}>
                  <IconButton onClick={() => setSelectedRound(null)}>
                    <ArrowBackIcon
                      sx={{
                        color: "white",
                        fontSize: theme.typography.pxToRem(32),
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Typography
                  sx={{
                    fontSize: {
                      xs: theme.typography.pxToRem(16),
                      md: theme.typography.pxToRem(32),
                    },
                    userSelect: "text",
                  }}
                >
                  {t("historyTable.roundDetails.id")}{" "}
                  {selectedRound.yantraExecutionId}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  p: { xs: 1, md: 0 },
                  userSelect: "text",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "normal",
                      userSelect: "text",
                    }}
                  >
                    {t("historyTable.roundDetails.id")}{" "}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "bold",
                      userSelect: "text",
                    }}
                  >
                    {selectedRound.yantraExecutionId}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "normal",
                      userSelect: "text",
                    }}
                  >
                    {t("historyTable.roundDetails.buyDate")}{" "}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "bold",
                      userSelect: "text",
                    }}
                  >
                    <FormattedDate value={selectedRound.buyDate} />{" "}
                    <FormattedTime
                      value={selectedRound.buyDate}
                      timeStyle="long"
                    />
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "normal",
                      userSelect: "text",
                    }}
                  >
                    {t("historyTable.roundDetails.betSum")}{" "}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "bold",
                      userSelect: "text",
                    }}
                  >
                    <FormattedNumber
                      value={
                        selectedRound.amount * selectedRound.price +
                        selectedRound.rowAmount * selectedRound.rowPrice
                      }
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                    />
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "normal",
                      userSelect: "text",
                    }}
                  >
                    {t("historyTable.winAmount")}{" "}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "bold",
                      userSelect: "text",
                    }}
                  >
                    <FormattedNumber
                      value={winAmount}
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                    />
                  </Typography>
                </Box>
              </Box>

              {executionLoading || !execution ? (
                <Loader isSmall />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {/* Winning info */}
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(14),
                      fontFamily: "sans-serif",
                      fontWeight: "bold",
                      userSelect: "text",
                    }}
                  >
                    {t("historyTable.roundDetails.winningYantra")}
                  </Typography>
                  {winningYantra ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        userSelect: "text",
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: winningColumn?.borderColor || "transparent",
                          p: 2,
                          borderRadius: 1.5,
                          boxShadow: "0px 0px 10px 3px #d3b978",
                        }}
                      >
                        <img
                          src={winningYantra.image}
                          alt={winningYantra.name}
                          width={100}
                          height={100}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: theme.typography.pxToRem(14),
                          fontFamily: "sans-serif",
                          fontWeight: "bold",
                          textAlign: "center",
                          userSelect: "text",
                        }}
                      >
                        {winningYantra.name}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography>
                      {t("historyTable.roundDetails.noWinningYantra")}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      width: "100%",
                      mt: 2,
                    }}
                  >
                    <MaterialReactTable table={roundBetsTable} />
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <ThemeProvider theme={localTheme}>
                <MRTBox>
                  <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      mb: 2,
                      "& .MuiTab-root": {
                        color: "white",
                        fontSize: (theme) => theme.typography.pxToRem(16),
                      },
                      "& .Mui-selected": {
                        color: "#d3b978 !important",
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#d3b978",
                      },
                    }}
                  >
                    <Tab label={t("tabs.history")} />
                    <Tab label={t("tabs.rules")} />
                  </Tabs>

                  {activeTab === 0 && (
                    <Box>
                      <MaterialReactTable table={table} />
                    </Box>
                  )}

                  {activeTab === 1 && (
                    <Box
                      sx={{
                        color: "white",
                        p: { xs: 2, md: 3 },
                        fontSize: (theme) => theme.typography.pxToRem(14),
                        maxHeight: "70vh",
                        overflowY: "auto",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ mb: 3, color: "#d3b978", fontWeight: "bold" }}
                      >
                        {t("tabs.rules")}
                      </Typography>

                      {/* Game Overview */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 1.5, color: "#d3b978", fontSize: "1.1rem" }}
                        >
                          {t("rules.overview.title")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          {t("rules.overview.description")}
                        </Typography>
                        {yantrasCount > 0 && rowsCount > 0 && (
                          <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                            {t("rules.overview.currentGame", {
                              yantras: yantrasCount,
                              rows: rowsCount,
                            })}
                          </Typography>
                        )}
                      </Box>

                      {/* How to Play */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 1.5, color: "#d3b978", fontSize: "1.1rem" }}
                        >
                          {t("rules.howToPlay.title")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • {t("rules.howToPlay.step1")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • {t("rules.howToPlay.step2")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • {t("rules.howToPlay.step3")}
                        </Typography>
                      </Box>

                      {/* Payouts */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 1.5, color: "#d3b978", fontSize: "1.1rem" }}
                        >
                          {t("rules.payouts.title")}
                        </Typography>
                        {yantraPayout > 0 && (
                          <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                            •{" "}
                            {t("rules.payouts.yantra", {
                              multiplier: formatNumber(yantraMultiplier, {
                                maximumFractionDigits: 2,
                              }),
                            })}
                          </Typography>
                        )}
                        {rowPayout > 0 && (
                          <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                            •{" "}
                            {t("rules.payouts.row", {
                              multiplier: formatNumber(rowMultiplier, {
                                maximumFractionDigits: 2,
                              }),
                            })}
                          </Typography>
                        )}
                        {yantraPayout > 0 && yantraPrice > 0 && (
                          <Typography
                            sx={{
                              mb: 1,
                              lineHeight: 1.6,
                              fontStyle: "italic",
                              color: "#aaa",
                            }}
                          >
                            {t("rules.payouts.example")}
                            <br />
                            {t("rules.payouts.yantraPrice", {
                              price: formatNumber(yantraPrice, {
                                maximumFractionDigits: 2,
                              }),
                              multiplier: formatNumber(yantraMultiplier, {
                                maximumFractionDigits: 2,
                              }),
                              total: formatNumber(yantraPayout, {
                                maximumFractionDigits: 2,
                              }),
                            })}
                            <br />
                            {t("rules.payouts.rowPrice", {
                              price: formatNumber(rowPrice, {
                                maximumFractionDigits: 2,
                              }),
                              multiplier: formatNumber(rowMultiplier, {
                                maximumFractionDigits: 2,
                              }),
                              total: formatNumber(rowPayout, {
                                maximumFractionDigits: 2,
                              }),
                            })}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            mb: 1,
                            mt: 2,
                            lineHeight: 1.6,
                            p: 2,
                            bgcolor: "rgba(211, 185, 120, 0.1)",
                            borderRadius: 1,
                            borderLeft: "3px solid #d3b978",
                          }}
                        >
                          💡 {t("rules.payouts.rowExplanation")}
                        </Typography>
                        <Typography
                          sx={{
                            mb: 1,
                            lineHeight: 1.6,
                            p: 2,
                            bgcolor: "rgba(76, 175, 80, 0.1)",
                            borderRadius: 1,
                            borderLeft: "3px solid #4caf50",
                          }}
                        >
                          ✨ {t("rules.payouts.bothWin")}
                        </Typography>
                      </Box>

                      {/* Control Buttons */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 1.5, color: "#d3b978", fontSize: "1.1rem" }}
                        >
                          {t("rules.controls.title")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • <strong>{t("controlBoard.double")}:</strong>{" "}
                          {t("rules.controls.double")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • <strong>{t("controlBoard.rebet")}:</strong>{" "}
                          {t("rules.controls.rebet")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • <strong>{t("controlBoard.clear")}:</strong>{" "}
                          {t("rules.controls.clear")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • <strong>{t("controlBoard.undo")}:</strong>{" "}
                          {t("rules.controls.undo")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          • <strong>{t("controlBoard.redo")}:</strong>{" "}
                          {t("rules.controls.redo")}
                        </Typography>
                      </Box>

                      {/* History */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 1.5, color: "#d3b978", fontSize: "1.1rem" }}
                        >
                          {t("rules.history.title")}
                        </Typography>
                        <Typography sx={{ mb: 1, lineHeight: 1.6 }}>
                          {t("rules.history.description")}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </MRTBox>
              </ThemeProvider>
            </LocalizationProvider>
          )}
        </Box>
      </Box>
    </Portal>
  );
}

export default HistoryTable;
